import { CounterpartyAddress } from "@common/schemas/counterpartyAddress.schema";
import { CounterpartyContact } from "@common/schemas/counterpartyContact.schema";
import { Entity } from "@common/types/entity";
import { formatCounterpartyContactName } from "@components/counterparty/counterpartyBookFormat";
import type { CounterpartyComboboxValue } from "@components/transactions/TransactionSplitV2/CounterpartyCombobox";
import {
    firstNonEmptyString,
    nonEmptyIdOrUndefined,
} from "./nativeInvoiceFormHelpers";
import {
    NativeInvoiceCustomer,
    NativeInvoiceFormRawState,
    NativeInvoiceFromState,
} from "./nativeInvoiceFormTypes";

export interface NativeInvoiceCustomerBooks {
    contact?: CounterpartyContact | null;
    address?: CounterpartyAddress | null;
}

export type SelectNativeInvoiceCustomer = (
    counterparty: CounterpartyComboboxValue | undefined,
    books?: NativeInvoiceCustomerBooks,
) => void;

interface InvoiceAddressParts {
    street?: string | null;
    street2?: string | null;
    city?: string | null;
    state?: string | null;
    postalCode?: string | null;
    country?: string | null;
}

export function fromStateFromEntity({
    entity,
    fallbackEmail,
    address,
}: {
    entity?: Entity;
    fallbackEmail?: string | null;
    address?: InvoiceAddressParts | null;
} = {}): NativeInvoiceFromState {
    // Workspace entity payloads omit the address relation, so callers pass the
    // dedicated address record when they have it. Fall back to entity.address
    // for tests and any payload that already includes it.
    const resolvedAddress = address ?? entity?.address ?? null;
    // The business entity has no email/phone of its own; its contact details
    // live on the owning user relation (loaded as `EntityWithUser`). We seed the
    // editable From block from there so the document reflects the sender's real
    // contact info. The email prefers the entity owner's address and falls back
    // to the current user's email (`fallbackEmail`) so the From email is
    // populated by default; only when neither exists do we seed an empty
    // string, which keeps the piece off the rendered invoice until the sender
    // types one. The phone is seeded empty when the owner has none.
    return {
        businessName: entity?.name ?? "",
        // The business entity has no separate contact-person name; seed empty
        // so the piece stays off the rendered invoice until the sender types
        // one.
        name: "",
        email: firstNonEmptyString(entity?.user?.email, fallbackEmail),
        phoneNumber:
            entity?.user?.phoneNumberDisplay ?? entity?.user?.phoneNumber ?? "",
        // Future: connect Tax ID/EIN to counterparty/entity data when available
        taxId: "",
        street: resolvedAddress?.street ?? "",
        street2: resolvedAddress?.street2 ?? "",
        city: resolvedAddress?.city ?? "",
        state: resolvedAddress?.state ?? "",
        postalCode: resolvedAddress?.postalCode ?? "",
        country: resolvedAddress?.country ?? "",
    };
}

export function applyAddressToFromState({
    from,
    address,
}: {
    from: NativeInvoiceFromState;
    address?: InvoiceAddressParts | null;
}): NativeInvoiceFromState {
    return {
        ...from,
        street: address?.street ?? "",
        street2: address?.street2 ?? "",
        city: address?.city ?? "",
        state: address?.state ?? "",
        postalCode: address?.postalCode ?? "",
        country: address?.country ?? "",
    };
}

function firstBookEntry<T extends { isPrimary: boolean }>(
    items: T[],
): T | null {
    return items.find((item) => item.isPrimary) ?? items[0] ?? null;
}

export function customerBooksFromDetails(details: {
    contacts: CounterpartyContact[];
    addresses: CounterpartyAddress[];
}): NativeInvoiceCustomerBooks {
    return {
        contact: firstBookEntry(details.contacts),
        address: firstBookEntry(details.addresses),
    };
}

function customerFromBooks({
    counterparty,
    existing,
    contact,
    address,
}: {
    counterparty: CounterpartyComboboxValue;
    existing: NativeInvoiceCustomer | null;
    contact?: CounterpartyContact | null;
    address?: CounterpartyAddress | null;
}): NativeInvoiceCustomer {
    const next: NativeInvoiceCustomer = {
        id: counterparty.id,
        name: counterparty.name,
        email:
            contact !== undefined
                ? (contact?.email ?? null)
                : (counterparty.email ?? null),
        cc: counterparty.cc ?? null,
        bcc: counterparty.bcc ?? null,
        phoneNumber:
            contact !== undefined
                ? (contact?.phoneNumber ?? null)
                : (counterparty.phoneNumber ?? null),
        street:
            address !== undefined
                ? (address?.street ?? null)
                : (counterparty.street ?? null),
        street2:
            address !== undefined
                ? (address?.street2 ?? null)
                : (counterparty.street2 ?? null),
        city:
            address !== undefined
                ? (address?.city ?? null)
                : (counterparty.city ?? null),
        state:
            address !== undefined
                ? (address?.state ?? null)
                : (counterparty.state ?? null),
        postalCode:
            address !== undefined
                ? (address?.postalCode ?? null)
                : (counterparty.postalCode ?? null),
        country:
            address !== undefined
                ? (address?.country ?? null)
                : (counterparty.country ?? null),
    };
    if (existing && existing.id === counterparty.id) {
        return { ...existing, ...next };
    }
    return next;
}

export function customerFormFieldsFromCounterparty({
    counterparty,
    existingCustomer,
    contact,
    address,
}: {
    counterparty: CounterpartyComboboxValue | undefined;
    existingCustomer: NativeInvoiceCustomer | null;
    contact?: CounterpartyContact | null;
    address?: CounterpartyAddress | null;
}): Pick<
    NativeInvoiceFormRawState,
    "customer" | "toFullName" | "toTaxId" | "toCompany"
> & {
    isSameCustomer: boolean;
} {
    const hasBooks = contact !== undefined || address !== undefined;
    const customer = counterparty
        ? customerFromBooks({
              counterparty,
              existing: existingCustomer,
              contact,
              address,
          })
        : null;
    const previousCustomerId = nonEmptyIdOrUndefined(existingCustomer?.id);
    const nextCustomerId = nonEmptyIdOrUndefined(customer?.id);
    const isSameCustomer = previousCustomerId === nextCustomerId && !hasBooks;
    if (isSameCustomer) {
        return {
            customer,
            toFullName: "",
            toTaxId: "",
            toCompany: "",
            isSameCustomer,
        };
    }

    // Company stays the counterparty company only. The PDF headline is the
    // display name (customer.name), so an empty company must not be backfilled
    // from that name — otherwise clearing Company would clear the printed To
    // name as well.
    const toCompany = firstNonEmptyString(counterparty?.companyName);
    // Always populate the Contact name field from the counterparty contact's
    // concatenated name fields (title/first/middle/last/suffix), even when it
    // happens to match the display name (e.g. a person with no separate
    // company) — Company and Contact name are distinct, user-editable
    // fields, and the PDF render already dedupes an identical raw name against
    // businessName so it never prints twice (see resolvePartyNameLine in
    // applyNativeInvoicePdfFieldSettings).
    const contactName = contact ? formatCounterpartyContactName(contact) : "";

    return {
        customer,
        toFullName: contactName,
        toTaxId: "",
        toCompany,
        isSameCustomer,
    };
}
